import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Gift, TrendingUp, ChevronRight } from "lucide-react";
import { sr } from "../../lib/sr";

const usd = (n) =>
  "$" + Math.round(Number(n || 0)).toLocaleString("en-US");

export default function BonusInventory({ compact = false }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    sr.bonus().then(setD).catch(() => setErr(true));
  }, []);

  if (err) return null;
  if (!d) {
    return (
      <div className="sr-card" data-testid="bonus-inventory-loading" style={{ marginTop: 14 }}>
        <div className="sr-muted" style={{ fontSize: 13 }}>Loading bonus inventory…</div>
      </div>
    );
  }

  const next = d.next_tier;
  const bandMax = (d.tiers?.[d.tiers.length - 1]?.from) || 500000;
  const pct = next
    ? Math.min(100, Math.round((d.delivered_media / next.at) * 100))
    : 100;

  const Header = (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <Gift size={18} color="var(--navy)" />
      <h3 style={{ fontSize: 16, margin: 0, color: "var(--navy)" }}>Annual Bonus Inventory</h3>
      <span className="sr-badge sr-badge-green" style={{ marginLeft: "auto", fontSize: 11 }}>
        {Math.round((d.current_rate || 0) * 100)}% tier
      </span>
    </div>
  );

  const Numbers = (
    <div style={{ display: "flex", gap: 18, marginTop: 12, flexWrap: "wrap" }}>
      <div>
        <div className="sr-muted" style={{ fontSize: 11 }}>Delivered media (12-mo)</div>
        <div data-testid="bonus-delivered" style={{ fontSize: 22, fontWeight: 800, color: "var(--navy)" }}>{usd(d.delivered_media)}</div>
      </div>
      <div>
        <div className="sr-muted" style={{ fontSize: 11 }}>Bonus media earned</div>
        <div data-testid="bonus-earned" style={{ fontSize: 22, fontWeight: 800, color: "#15803d" }}>{usd(d.bonus_total)}</div>
      </div>
    </div>
  );

  const Progress = next ? (
    <div style={{ marginTop: 12 }} data-testid="bonus-next-tier">
      <div className="sr-muted" style={{ fontSize: 12, marginBottom: 6 }}>
        <TrendingUp size={12} /> {usd(next.remaining)} more delivered media unlocks the <strong>{Math.round(next.rate * 100)}%</strong> tier
      </div>
      <div style={{ height: 8, background: "#e5e7eb", borderRadius: 6, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: "linear-gradient(90deg,#16a34a,#22c55e)" }} />
      </div>
    </div>
  ) : (
    <div className="sr-muted" style={{ fontSize: 12, marginTop: 12 }} data-testid="bonus-top-tier">
      🎉 You're at the top bonus tier — every dollar above {usd(bandMax)} earns the maximum rate.
    </div>
  );

  if (compact) {
    return (
      <div className="sr-card" data-testid="bonus-inventory-compact" style={{ marginTop: 14 }}>
        {Header}
        {Numbers}
        {Progress}
        <Link to="/app/bonus" className="sr-btn sr-btn-ghost" data-testid="bonus-view-details" style={{ marginTop: 12 }}>
          View bonus details <ChevronRight size={15} />
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="bonus-inventory">
      <div className="sr-card">
        {Header}
        <p className="sr-muted" style={{ fontSize: 13, marginTop: 8 }} data-testid="bonus-note">{d.note}</p>
        <div className="sr-muted" style={{ fontSize: 12 }}>
          Window: {String(d.window?.start).slice(0, 10)} → {String(d.window?.end).slice(0, 10)}
        </div>
        {Numbers}
        {Progress}
      </div>

      <div className="sr-card" style={{ marginTop: 14 }}>
        <h4 style={{ margin: "0 0 10px", color: "var(--navy)" }}>Tier ladder</h4>
        {(d.bands || []).map((b, i) => (
          <div key={i} data-testid={`bonus-band-${i}`} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid #f1f5f9" }}>
            <span className="sr-badge sr-badge-gray" style={{ minWidth: 48, textAlign: "center" }}>{Math.round(b.rate * 100)}%</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, color: "var(--navy)", fontSize: 13 }}>
                {usd(b.from)} {b.to != null ? `– ${usd(b.to)}` : "and above"}
              </div>
              <div className="sr-muted" style={{ fontSize: 12 }}>Delivered in band: {usd(b.eligible)}</div>
            </div>
            <div style={{ fontWeight: 700, color: b.bonus > 0 ? "#15803d" : "var(--muted)" }}>+{usd(b.bonus)}</div>
          </div>
        ))}
      </div>

      <div className="sr-card" style={{ marginTop: 14 }}>
        <h4 style={{ margin: "0 0 10px", color: "var(--navy)" }}>Delivered media ledger</h4>
        {(d.entries || []).length === 0 ? (
          <div className="sr-muted" style={{ fontSize: 13 }}>No delivered media recorded in this window yet. Bonus accrues as your campaigns deliver.</div>
        ) : (
          (d.entries || []).slice().reverse().map((e, i) => (
            <div key={e.id || i} data-testid={`bonus-entry-${i}`} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid #f1f5f9", fontSize: 13 }}>
              <span className="sr-muted">{String(e.date).slice(0, 10)} · {e.note || e.source}</span>
              <strong style={{ color: "var(--navy)" }}>{usd(e.media_amount)}</strong>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
